import { IconWrapper } from '@flexo/general';
import { KPI, store } from '@flexo/reducers';
import { TableCell, TableSortLabel } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TableSkeleton } from '../skeletons/table-skeleton';
import {
  Collapsible,
  createGetCellContent,
  openPdfWithTitle,
  sortRows,
  transformRealDataToStructuredData,
} from './helpers';
import styles from './table.module.scss';

/**
 * SitesPaymentsTable component displays payments data grouped by month
 * Each month shows a list of sites with their payment information
 */
export function SitePaymentsTable(props: any) {
  const { Table__NO_DATA } = styles;
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const members = metadata?.entities?.members || [];

  // Translations and data
  const { t } = useTranslation();
  const { title, no_data_text, rows } = t(`widgets.tables.sitePaymentsTable`, {
    returnObjects: true,
  }) as any;

  // State for sorting
  const [rowsOrder, setRowsOrder] = useState<[string, string]>([
    'invoiceID',
    'asc',
  ]);
  const [storedRows, setStoredRows] = useState<any[]>([]);

  const [isExpanded, setIsExpanded] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState<string | null>(null);
  const dispatch = store.dispatch;
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const titlePlaceholderRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerPlaceholderRef = useRef<HTMLDivElement>(null);
  const collapseSectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const collapseTitleRefs = useRef<Array<HTMLDivElement | null>>([]);
  const collapsePlaceholderRefs = useRef<Array<HTMLDivElement | null>>([]);

  const handleDownloadInvoice = async (siteData: any) => {
    // If file_base64 is already available (legacy), use it directly
    if (siteData?.file_base64) {
      openPdfWithTitle(
        siteData.file_base64,
        siteData?.invoiceID || siteData?.fileName || 'Document'
      );
      return;
    }

    // Otherwise, fetch on-demand
    const invoiceId = siteData?.invoice_no || siteData?.invoiceID;
    setLoadingInvoice(invoiceId);
    try {
      const result: any = await dispatch(
        KPI.FetchKwickInvoiceFileAsync({
          invoiceNo: invoiceId,
          siteId: siteData?.siteId,
        }) as any
      );
      if (result.payload?.file_base64) {
        openPdfWithTitle(
          result.payload.file_base64,
          siteData?.invoiceID || siteData?.fileName || 'Document'
        );
      }
    } finally {
      setLoadingInvoice(null);
    }
  };

  /**
   * Handle sorting using the helper function
   * @param columnId - The column ID to sort by
   */
  function handleSort(columnId: string) {
    const newRows = sortRows(storedRows, columnId, rowsOrder, setRowsOrder);
    setStoredRows(newRows);
  }

  // Use the factory function to create a getCellContent with styling from styles and members array
  // Passing the full members array allows per-row member resolution via invoice userId
  const getCellContent = createGetCellContent(
    t,
    false,
    members,
    handleDownloadInvoice,
    loadingInvoice
  );

  useEffect(() => {
    if (!kpi?.kwickInvoices || !Array.isArray(kpi.kwickInvoices)) {
      setStoredRows([]);
      return;
    }

    if (kpi.kwickInvoices.length === 0) {
      setStoredRows([]);
      return;
    }

    setStoredRows(
      transformRealDataToStructuredData(kpi?.kwickInvoices, metadata) as any
    );
  }, [kpi?.kwickInvoices, metadata]);

  useEffect(() => {
    if (storedRows?.length === 0) {
      return;
    }

    setIsExpanded(
      Array.from({ length: storedRows?.length }, () => true).reduce(
        (acc: any, curr: any, index: number) => {
          acc[`Q${index + 1}`] = curr;
          return acc;
        },
        {}
      )
    );
  }, [storedRows?.length]);

  useEffect(() => {
    let rafId: number | null = null;
    let scrollContainer: HTMLElement | null = null;
    let fixedHeaderBottom = 0;

    const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;

      const ionContent = el.closest('ion-content') as any;
      if (ionContent?.shadowRoot) {
        const innerScroll = ionContent.shadowRoot.querySelector('.inner-scroll');
        if (innerScroll) return innerScroll as HTMLElement;
      }

      let parent: HTMLElement | null = el.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }

      return null;
    };

    const measureFixedHeaderBottom = (anchor: HTMLElement): number => {
      let maxBottom = 0;

      const check = (el: Element) => {
        const computedStyles = window.getComputedStyle(el as HTMLElement);
        if (computedStyles.position !== 'fixed') return;

        const rect = (el as HTMLElement).getBoundingClientRect();
        if (rect.top <= 2 && rect.height > 0 && rect.bottom > maxBottom) {
          maxBottom = rect.bottom;
        }
      };

      let node: HTMLElement | null = anchor;
      while (node) {
        let sibling = node.previousElementSibling;
        while (sibling) {
          check(sibling);
          sibling.querySelectorAll('*').forEach(check);
          sibling = sibling.previousElementSibling;
        }
        node = node.parentElement;
      }

      return maxBottom;
    };

    const refreshFixedHeader = () => {
      if (!titleRef.current) return;
      fixedHeaderBottom = measureFixedHeaderBottom(titleRef.current);
    };

    const setPlaceholderHeight = (
      placeholder: HTMLElement | null,
      height: number
    ) => {
      if (!placeholder) return;
      placeholder.style.setProperty(
        '--table-sticky-placeholder-height',
        `${height}px`
      );
    };

    const stick = (
      el: HTMLElement | null,
      placeholder: HTMLElement | null,
      {
        top,
        left,
        width,
        zIndex,
        clipPath,
        variantClass,
      }: {
        top: number;
        left: number;
        width: number;
        zIndex: number;
        clipPath?: string;
        variantClass?: string;
      }
    ) => {
      if (!el) return;

      if (placeholder) {
        setPlaceholderHeight(placeholder, el.offsetHeight);
      }

      el.classList.add(styles.Table__Sticky);
      if (variantClass) {
        el.classList.add(variantClass);
      }

      el.style.setProperty('--table-sticky-top', `${top}px`);
      el.style.setProperty('--table-sticky-left', `${left}px`);
      el.style.setProperty('--table-sticky-width', `${width}px`);
      el.style.setProperty('--table-sticky-z-index', `${zIndex}`);

      if (clipPath) {
        el.style.setProperty('--table-sticky-clip-path', clipPath);
      } else {
        el.style.removeProperty('--table-sticky-clip-path');
      }
    };

    const unstick = (el: HTMLElement | null, placeholder: HTMLElement | null) => {
      if (el) {
        el.classList.remove(
          styles.Table__Sticky,
          styles.Table__Sticky__Title,
          styles.Table__Sticky__Header,
          styles.Table__Sticky__Collapse
        );
        el.style.removeProperty('--table-sticky-top');
        el.style.removeProperty('--table-sticky-left');
        el.style.removeProperty('--table-sticky-width');
        el.style.removeProperty('--table-sticky-z-index');
        el.style.removeProperty('--table-sticky-clip-path');
      }

      if (placeholder) {
        placeholder.style.removeProperty('--table-sticky-placeholder-height');
      }
    };

    const update = () => {
      const section = sectionRef.current;
      const title = titleRef.current;
      const titlePlaceholder = titlePlaceholderRef.current;
      const overflow = overflowRef.current;
      const header = headerRef.current;
      const headerPlaceholder = headerPlaceholderRef.current;

      if (
        !section ||
        !title ||
        !titlePlaceholder ||
        !overflow ||
        !header ||
        !headerPlaceholder
      ) {
        return;
      }

      if (!scrollContainer) {
        scrollContainer = findScrollContainer(section);
      }

      fixedHeaderBottom = measureFixedHeaderBottom(title);

      const scrollViewportTop = scrollContainer
        ? scrollContainer.getBoundingClientRect().top
        : 0;
      const currentScrollTop = scrollContainer?.scrollTop || 0;
      const maxScrollTop = scrollContainer
        ? Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight)
        : 0;
      const isAtScrollEnd = currentScrollTop >= maxScrollTop - 1;
      const baseline = Math.max(scrollViewportTop, fixedHeaderBottom);

      const sectionRect = section.getBoundingClientRect();
      const titlePlaceholderRect = titlePlaceholder.getBoundingClientRect();
      const headerPlaceholderRect = headerPlaceholder.getBoundingClientRect();
      const overflowRect = overflow.getBoundingClientRect();

      const titleHeight = title.offsetHeight;
      const headerHeight = header.offsetHeight;
      const stackedHeight = titleHeight + headerHeight;

      const titleShouldStick =
        (titlePlaceholderRect.top <= baseline ||
          (isAtScrollEnd && titlePlaceholderRect.top <= baseline + titleHeight)) &&
        sectionRect.bottom > baseline + stackedHeight;

      if (titleShouldStick) {
        stick(title, titlePlaceholder, {
          top: baseline,
          left: titlePlaceholderRect.left,
          width: titlePlaceholderRect.width,
          zIndex: 12,
          variantClass: styles.Table__Sticky__Title,
        });
      } else {
        unstick(title, titlePlaceholder);
      }

      const headerTop = baseline + (titleShouldStick ? titleHeight : 0);
      const headerShouldStick =
        titleShouldStick &&
        (headerPlaceholderRect.top <= headerTop ||
          (isAtScrollEnd &&
            headerPlaceholderRect.top <= headerTop + headerHeight)) &&
        sectionRect.bottom > headerTop + headerHeight;

      if (headerShouldStick) {
        const headerTable = header.querySelector('table') as HTMLElement | null;
        const stickyWidth =
          overflow.scrollWidth || headerTable?.offsetWidth || header.offsetWidth;

        const headerLeft = headerPlaceholderRect.left;
        const headerRight = headerLeft + stickyWidth;
        const clipLeft = Math.max(0, overflowRect.left - headerLeft);
        const clipRight = Math.max(0, headerRight - overflowRect.right);
        stick(header, headerPlaceholder, {
          top: headerTop,
          left: headerPlaceholderRect.left,
          width: stickyWidth,
          zIndex: 11,
          clipPath: `inset(0 ${clipRight}px 0 ${clipLeft}px)`,
          variantClass: styles.Table__Sticky__Header,
        });
      } else {
        unstick(header, headerPlaceholder);
      }

      const collapseTop =
        baseline +
        (titleShouldStick ? titleHeight : 0) +
        (headerShouldStick ? headerHeight : 0);
      const collapseSwitchTolerance = 3;

      const collapseEntries = collapseSectionRefs.current
        .map((collapseSection, index) => {
          const collapseTitle = collapseTitleRefs.current[index];
          const collapsePlaceholder = collapsePlaceholderRefs.current[index];

          if (!collapseSection || !collapseTitle || !collapsePlaceholder) {
            return null;
          }

          return {
            index,
            collapseSection,
            collapseTitle,
            collapsePlaceholder,
            sectionRect: collapseSection.getBoundingClientRect(),
            placeholderRect: collapsePlaceholder.getBoundingClientRect(),
            height: collapseTitle.offsetHeight,
          };
        })
        .filter(Boolean) as Array<{
        index: number;
        collapseSection: HTMLDivElement;
        collapseTitle: HTMLDivElement;
        collapsePlaceholder: HTMLDivElement;
        sectionRect: DOMRect;
        placeholderRect: DOMRect;
        height: number;
      }>;

      let activeCollapseIndex = -1;
      collapseEntries.forEach((entry) => {
        const collapseReachedStickyZone =
          entry.placeholderRect.top <=
          collapseTop + collapseSwitchTolerance;

        if (collapseReachedStickyZone) {
          activeCollapseIndex = entry.index;
        }
      });

      collapseEntries.forEach((entry) => {
        if (entry.index !== activeCollapseIndex) {
          unstick(entry.collapseTitle, entry.collapsePlaceholder);
          return;
        }

        const stickyTop = collapseTop;

        if (entry.sectionRect.bottom <= stickyTop) {
          unstick(entry.collapseTitle, entry.collapsePlaceholder);
          return;
        }

        const stickyWidth = overflowRect.width;
        const collapseLeft = entry.placeholderRect.left;
        const collapseRight = collapseLeft + stickyWidth;
        const clipLeft = Math.max(0, overflowRect.left - collapseLeft);
        const clipRight = Math.max(0, collapseRight - overflowRect.right);

        stick(entry.collapseTitle, entry.collapsePlaceholder, {
          top: stickyTop,
          left: entry.placeholderRect.left,
          width: stickyWidth,
          zIndex: 10,
          clipPath: `inset(0 ${clipRight}px 0 ${clipLeft}px)`,
          variantClass: styles.Table__Sticky__Title,
        });
      });
    };

    const onResize = () => {
      scrollContainer = null;
      refreshFixedHeader();
    };

    refreshFixedHeader();
    window.addEventListener('resize', onResize);
    const poll = () => {
      update();
      rafId = window.requestAnimationFrame(poll);
    };

    rafId = window.requestAnimationFrame(poll);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }

      window.removeEventListener('resize', onResize);
      unstick(titleRef.current, titlePlaceholderRef.current);
      unstick(headerRef.current, headerPlaceholderRef.current);
      collapseTitleRefs.current.forEach((collapseTitle, index) => {
        unstick(collapseTitle, collapsePlaceholderRefs.current[index]);
      });
    };
  }, []);

  return (
    <>
      {props?.showTable !== false &&
      metadata &&
      storedRows?.length > 0 &&
      isExpanded ? (
        <div ref={sectionRef}>
          <div
            ref={titlePlaceholderRef}
            className={styles.Table__StickyPlaceholder}
            aria-hidden="true"
          ></div>
          <div ref={titleRef} className={`${styles.Table__Title}`}>
            {title}
          </div>
          <div
            ref={overflowRef}
            className={`${styles.Table__OverflowContainer} ${styles.Table__Wider}`}
          >
            <div
              ref={headerPlaceholderRef}
              className={styles.Table__StickyPlaceholder}
              aria-hidden="true"
            ></div>
            <div
              ref={headerRef}
              className={`${styles.Table} ${styles.Table__Flex} ${styles.Table__Payments}`}
            >
              <TableContainer
                component={Paper}
                key={`site_payments_table_header`}
              >
                <Table aria-label="simple table">
                  <TableHead key={`site_payments_table_head_header`}>
                    <TableRow>
                      {rows &&
                        rows.map((column: any, column_i: number) => (
                          <TableCell
                            className={`
                            ${
                              column.sortable
                                ? styles.Table__Header__Cell__Sortable
                                : styles.Table__Header__Cell__Static
                            }
                            ${
                              column.sortable && rowsOrder[0] === column.field
                                ? styles.Table__Header__Cell__Active
                                : styles.Table__Header__Cell
                            }`}
                            key={`site_payments_table_${column_i}`}
                          >
                            <TableSortLabel
                              active={
                                column.sortable && rowsOrder[0] === column.field
                              }
                              direction={
                                rowsOrder[0] === column.field &&
                                rowsOrder[1] === 'asc'
                                  ? 'asc'
                                  : 'desc'
                              }
                              onClick={() =>
                                column.sortable
                                  ? handleSort(column.field)
                                  : null
                              }
                              className={`${styles.Table__Header__Cell}`}
                              IconComponent={() =>
                                rowsOrder[0] === column.field ? (
                                  rowsOrder[1] === 'asc' ? (
                                    <IconWrapper
                                      iconName="chevron-up"
                                      className={`${styles.Table__TH__Icon}`}
                                    />
                                  ) : (
                                    <IconWrapper
                                      iconName="chevron-down"
                                      className={`${styles.Table__TH__Icon}`}
                                    />
                                  )
                                ) : (
                                  <div style={{ height: 32, width: 19 }}></div>
                                )
                              }
                            >
                              <div>{column.title}</div>
                            </TableSortLabel>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </div>

            {storedRows?.map((month: any, month_i: number) => (
              <div
                key={`${month.month}_table_container`}
                ref={(el) => {
                  collapseSectionRefs.current[month_i] = el;
                }}
                className={`${styles.Table} ${styles.Table__Flex} ${styles.Table__Payments}`}
              >
                <div
                  ref={(el) => {
                    collapsePlaceholderRefs.current[month_i] = el;
                  }}
                  className={styles.Table__StickyPlaceholder}
                  aria-hidden="true"
                ></div>
                <TableContainer
                  component={Paper}
                  ref={(el) => {
                    collapseTitleRefs.current[month_i] = el;
                  }}
                  key={`payments_table_${month.month}_table_heading_controller`}
                  className={`${styles.Table__Payments__Collapser} ${
                    isExpanded[`Q${month_i + 1}`]
                      ? styles.Table__Payments__Collapser__Expanded
                      : styles.Table__Payments__Collapser__Collapsed
                  }`}
                  onClick={() =>
                    setIsExpanded((prev: any) => ({
                      ...prev,
                      [`Q${month_i + 1}`]: !prev[`Q${month_i + 1}`],
                    }))
                  }
                >
                  <Table aria-label="simple table">
                    <TableHead key={`${month.month}_table_head_controller`}>
                      <TableRow>
                        <TableCell
                          className={styles.Table__Payments__Cell__Month}
                        >
                          {month.month}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                          {(month?.sites || []).reduce(
                            (acc: any, site: any) => acc + (site.paid ? 1 : 0),
                            0
                          )}
                          {'/'}
                          {month.sites?.length || 0}{' '}
                          {t(`widgets.tables.paymentsTable.paidInvoices`)}
                        </TableCell>

                        <TableCell>
                          <IconWrapper iconName="chevron-down" />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>

                <Collapsible
                  key={`site_payments_table_collapsible_${month.month}`}
                  open={isExpanded[`Q${month_i + 1}`]}
                >
                  <TableContainer
                    component={Paper}
                    key={`${month.month}_table`}
                  >
                    <Table aria-label="simple table">
                      <TableBody>
                        {month.sites.map((site, siteIndex) => (
                          <TableRow
                            key={`${month.month}_site_${siteIndex}`}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                            }}
                            style={{ height: 96 }}
                          >
                            {rows.map((column: any, columnIndex: number) => (
                              <TableCell
                                key={`${month.month}_site_${siteIndex}_col_${columnIndex}`}
                                className={`${
                                  !rows?.[columnIndex]?.sortable === true &&
                                  styles.Table__Cell__Static
                                }`}
                                component="th"
                                scope="row"
                              >
                                <div>{getCellContent(column, site)}</div>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapsible>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className={`${styles.Table} mb-3`}
          key={Math.floor(Math.random() * 100)}
        >
          <div className={`${styles.Table__Title} heading4`}>{title}</div>
          <TableSkeleton
            rows={rows}
            context="sitePayments"
            hasShimmer={false}
          />
        </div>
      )}
    </>
  );
}

export default SitePaymentsTable;
